import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import { Calendar } from "../../components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Loader2,
  CalendarDays,
  Clock,
  Wrench,
  User,
  Phone,
  Mail,
  Smartphone,
  CheckCircle2,
  AlertCircle,
  MapPin,
  ArrowRight,
  ArrowLeft,
  Store,
  Laptop,
  Tablet,
  Monitor,
  Printer,
  Server,
  HelpCircle,
  Gamepad2,
  Watch,
  Wifi,
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

// Device types with icons
const DEVICE_TYPES = [
  { value: "Mobile Phone", label: "Mobile Phone", icon: Smartphone, color: "bg-blue-100 text-blue-600" },
  { value: "Tablet", label: "Tablet", icon: Tablet, color: "bg-purple-100 text-purple-600" },
  { value: "Laptop", label: "Laptop", icon: Laptop, color: "bg-green-100 text-green-600" },
  { value: "Desktop", label: "Desktop", icon: Monitor, color: "bg-orange-100 text-orange-600" },
  { value: "Mini PC / SFF", label: "Mini PC", icon: Server, color: "bg-cyan-100 text-cyan-600" },
  { value: "Printer", label: "Printer", icon: Printer, color: "bg-pink-100 text-pink-600" },
  { value: "Scanner", label: "Scanner", icon: Printer, color: "bg-indigo-100 text-indigo-600" },
  { value: "Server", label: "Server", icon: Server, color: "bg-red-100 text-red-600" },
  { value: "Networking Equipment", label: "Networking", icon: Wifi, color: "bg-teal-100 text-teal-600" },
  { value: "Smartwatch", label: "Smartwatch", icon: Watch, color: "bg-amber-100 text-amber-600" },
  { value: "Gaming Console", label: "Gaming Console", icon: Gamepad2, color: "bg-violet-100 text-violet-600" },
  { value: "Other", label: "Other Device", icon: HelpCircle, color: "bg-gray-100 text-gray-600" },
];

// Map device types to service categories
const DEVICE_TO_CATEGORY = {
  "Mobile Phone": "mobile",
  "Tablet": "mobile",
  "Laptop": "laptop",
  "Desktop": "laptop",
  "Mini PC / SFF": "laptop",
  "All-in-One PC": "laptop",
};

export default function BookAppointment() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // New step order: 1=Device, 2=Services, 3=DateTime, 4=Contact, 5=Review
  const [step, setStep] = useState(1);
  
  // Get subdomain from URL or query param
  const getSubdomain = () => {
    const querySubdomain = searchParams.get("shop");
    if (querySubdomain) return querySubdomain;
    
    const hostname = window.location.hostname;
    const parts = hostname.split(".");
    if (parts.length >= 3 && parts[0] !== "www") {
      return parts[0];
    }
    return null;
  };
  
  const subdomain = getSubdomain();

  // Shop data
  const [shopInfo, setShopInfo] = useState(null);
  const [services, setServices] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [error, setError] = useState(null);

  // Form data
  const [selectedDeviceType, setSelectedDeviceType] = useState(null);
  const [otherDeviceType, setOtherDeviceType] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedServices, setSelectedServices] = useState([]);
  const [hasOtherIssue, setHasOtherIssue] = useState(false);
  const [otherIssueDescription, setOtherIssueDescription] = useState("");
  const [formData, setFormData] = useState({
    customer_name: "",
    customer_mobile: "",
    customer_email: "",
    brand: "",
    model: "",
  });

  // Booking confirmation
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);

  useEffect(() => {
    if (subdomain) {
      fetchShopInfo();
    } else {
      setError("No shop specified. Please use a valid booking link.");
      setLoading(false);
    }
  }, [subdomain]);

  useEffect(() => {
    if (selectedDate && subdomain && shopInfo?.is_booking_enabled) {
      fetchAvailableSlots(selectedDate);
    }
  }, [selectedDate, subdomain]);

  const fetchShopInfo = async () => {
    try {
      const [infoRes, servicesRes] = await Promise.all([
        axios.get(`${API}/api/public/shop/${subdomain}/info`),
        axios.get(`${API}/api/public/shop/${subdomain}/services`).catch(() => ({ data: { services: [] } })),
      ]);

      setShopInfo(infoRes.data);
      setServices(servicesRes.data.services || []);

      if (!infoRes.data.is_booking_enabled) {
        setError("Online booking is currently not available for this shop.");
      }
    } catch (err) {
      console.error("Failed to fetch shop info:", err);
      setError("Shop not found. Please check the booking link.");
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableSlots = async (date) => {
    // Format date as YYYY-MM-DD in local timezone (not UTC)
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    try {
      const res = await axios.get(`${API}/api/public/shop/${subdomain}/available-slots?date=${dateStr}`);
      setAvailableSlots(res.data.available_slots || []);
      
      if (res.data.is_blocked) {
        toast.error(res.data.block_reason || "This date is not available");
      } else if (res.data.is_closed) {
        toast.info("Shop is closed on this day");
      } else if (res.data.error) {
        toast.error(res.data.error);
      }
    } catch (err) {
      console.error("Failed to fetch slots:", err);
      setAvailableSlots([]);
    }
  };

  const handleServiceToggle = (serviceId) => {
    setSelectedServices((prev) =>
      prev.includes(serviceId)
        ? prev.filter((id) => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  // Get services filtered by device type
  const getFilteredServices = () => {
    if (!selectedDeviceType) return [];
    
    const category = DEVICE_TO_CATEGORY[selectedDeviceType];
    if (category) {
      return services.filter(s => s.category === category || s.category === "general");
    }
    // For other device types, show general services
    return services.filter(s => s.category === "general");
  };

  const handleSubmit = async () => {
    // Validate form
    if (!formData.customer_name.trim()) {
      toast.error("Please enter your name");
      return;
    }
    if (!formData.customer_mobile.trim() || formData.customer_mobile.length < 10) {
      toast.error("Please enter a valid phone number");
      return;
    }
    if (!selectedDeviceType) {
      toast.error("Please select device type");
      return;
    }
    if (selectedServices.length === 0 && !hasOtherIssue) {
      toast.error("Please select at least one service or describe your issue");
      return;
    }
    if (hasOtherIssue && !otherIssueDescription.trim()) {
      toast.error("Please describe your issue");
      return;
    }
    if (!selectedDate || !selectedSlot) {
      toast.error("Please select date and time");
      return;
    }

    setSubmitting(true);
    try {
      // Build problem description
      let problemDesc = "";
      const selectedServiceNames = selectedServices.map(id => {
        const svc = services.find(s => s.id === id);
        return svc?.name || "";
      }).filter(Boolean);
      
      if (selectedServiceNames.length > 0) {
        problemDesc = `Services needed: ${selectedServiceNames.join(", ")}`;
      }
      if (hasOtherIssue && otherIssueDescription.trim()) {
        if (problemDesc) problemDesc += "\n\n";
        problemDesc += `Additional issue: ${otherIssueDescription}`;
      }

      const deviceType = selectedDeviceType === "Other" && otherDeviceType.trim() 
        ? otherDeviceType 
        : selectedDeviceType;

      const bookingData = {
        customer_name: formData.customer_name,
        customer_mobile: formData.customer_mobile,
        customer_email: formData.customer_email || null,
        device_type: deviceType,
        brand: formData.brand || null,
        model: formData.model || null,
        service_ids: selectedServices,
        problem_description: problemDesc || "General checkup requested",
        // Format date in local timezone to avoid UTC conversion issues
        appointment_date: `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.getDate()).padStart(2, '0')}`,
        appointment_slot: `${selectedSlot.start_time}-${selectedSlot.end_time}`,
      };

      const res = await axios.post(`${API}/api/public/shop/${subdomain}/book`, bookingData);
      setBookingResult(res.data);
      setShowConfirmation(true);
    } catch (err) {
      console.error("Booking failed:", err);
      toast.error(err.response?.data?.detail || "Failed to create booking. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Disable dates before today and beyond max advance days
  const disableDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + (shopInfo?.max_advance_days || 30));
    
    return date < today || date > maxDate;
  };

  const filteredServices = getFilteredServices();

  const goBack = () => {
    // Navigate back with shop param
    const shopParam = searchParams.get("shop");
    if (shopParam) {
      navigate(`/?shop=${shopParam}`);
    } else {
      navigate("/");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Booking Unavailable</h2>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="customer-booking-page">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={goBack}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            {shopInfo?.logo_url ? (
              <img
                src={shopInfo.logo_url.startsWith("http") ? shopInfo.logo_url : `${API.replace("/api", "")}${shopInfo.logo_url}`}
                alt={shopInfo.shop_name}
                className="w-10 h-10 object-contain rounded-lg"
              />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Store className="w-5 h-5 text-primary" />
              </div>
            )}
            <div>
              <h1 className="font-bold text-lg">{shopInfo?.shop_name || "Book Appointment"}</h1>
              {shopInfo?.address && (
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {shopInfo.address}
                </p>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 mb-6">
          {["Device", "Service", "Date/Time", "Contact", "Review"].map((label, idx) => {
            const stepNum = idx + 1;
            return (
              <React.Fragment key={stepNum}>
                <div className="flex flex-col items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                      step >= stepNum
                        ? "bg-primary text-primary-foreground"
                        : "bg-gray-200 text-gray-500"
                    }`}
                  >
                    {stepNum}
                  </div>
                  <span className="text-xs mt-1 text-muted-foreground hidden sm:block">{label}</span>
                </div>
                {stepNum < 5 && (
                  <div className={`w-6 h-0.5 ${step > stepNum ? "bg-primary" : "bg-gray-200"}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Step 1: Device Type Selection */}
        {step === 1 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-primary" />
                What device needs repair?
              </CardTitle>
              <CardDescription>Select your device type</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {DEVICE_TYPES.map((device) => {
                  const Icon = device.icon;
                  const isSelected = selectedDeviceType === device.value;
                  return (
                    <button
                      key={device.value}
                      onClick={() => setSelectedDeviceType(device.value)}
                      className={`p-4 rounded-xl border-2 transition-all text-center ${
                        isSelected
                          ? "border-primary bg-primary/5 shadow-md"
                          : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                      }`}
                      data-testid={`device-${device.value.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <div className={`w-12 h-12 rounded-full ${device.color} flex items-center justify-center mx-auto mb-2`}>
                        <Icon className="w-6 h-6" />
                      </div>
                      <p className="font-medium text-sm">{device.label}</p>
                    </button>
                  );
                })}
              </div>

              {/* Other device type input */}
              {selectedDeviceType === "Other" && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <Label>Describe your device</Label>
                  <Input
                    value={otherDeviceType}
                    onChange={(e) => setOtherDeviceType(e.target.value)}
                    placeholder="e.g., Smart TV, Air Purifier, etc."
                    className="mt-2"
                  />
                </div>
              )}

              <div className="flex justify-end mt-6">
                <Button
                  onClick={() => setStep(2)}
                  disabled={!selectedDeviceType || (selectedDeviceType === "Other" && !otherDeviceType.trim())}
                  data-testid="next-step-btn"
                >
                  Continue
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Select Services (filtered by device) */}
        {step === 2 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wrench className="w-5 h-5 text-primary" />
                What's the issue?
              </CardTitle>
              <CardDescription>
                Select the services you need for your {selectedDeviceType}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {filteredServices.length > 0 && (
                <div className="grid sm:grid-cols-2 gap-2 mb-4">
                  {filteredServices.map((service) => (
                    <div
                      key={service.id}
                      onClick={() => handleServiceToggle(service.id)}
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedServices.includes(service.id)
                          ? "bg-primary/5 border-primary"
                          : "hover:bg-accent"
                      }`}
                    >
                      <Checkbox
                        checked={selectedServices.includes(service.id)}
                        onChange={() => {}}
                      />
                      <div className="flex-1">
                        <p className="font-medium text-sm">{service.name}</p>
                        {service.description && (
                          <p className="text-xs text-muted-foreground">{service.description}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Other issue option */}
              <div className="border-t pt-4">
                <div
                  onClick={() => setHasOtherIssue(!hasOtherIssue)}
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    hasOtherIssue ? "bg-orange-50 border-orange-300" : "hover:bg-accent"
                  }`}
                >
                  <Checkbox checked={hasOtherIssue} onChange={() => {}} />
                  <div className="flex-1">
                    <p className="font-medium text-sm flex items-center gap-2">
                      <HelpCircle className="w-4 h-4 text-orange-500" />
                      Other Issue / Not Listed Above
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Describe your problem in your own words
                    </p>
                  </div>
                </div>

                {hasOtherIssue && (
                  <div className="mt-3 ml-8">
                    <Textarea
                      value={otherIssueDescription}
                      onChange={(e) => setOtherIssueDescription(e.target.value)}
                      placeholder="Please describe your issue in detail..."
                      rows={3}
                      data-testid="other-issue-input"
                    />
                  </div>
                )}
              </div>

              {filteredServices.length === 0 && !hasOtherIssue && (
                <div className="text-center py-4 text-muted-foreground">
                  <p>No specific services available for this device type.</p>
                  <p className="text-sm">Please select "Other Issue" to describe your problem.</p>
                </div>
              )}

              <div className="flex justify-between mt-6">
                <Button variant="outline" onClick={() => setStep(1)}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button
                  onClick={() => setStep(3)}
                  disabled={selectedServices.length === 0 && !hasOtherIssue}
                  data-testid="next-step-btn"
                >
                  Continue
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Date & Time Selection */}
        {step === 3 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-primary" />
                When would you like to visit?
              </CardTitle>
              <CardDescription>Choose your preferred date and time</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-6">
                {/* Calendar */}
                <div>
                  <Label className="mb-2 block">Select Date</Label>
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => {
                      setSelectedDate(date);
                      setSelectedSlot(null);
                    }}
                    disabled={disableDate}
                    className="rounded-md border"
                  />
                </div>

                {/* Time Slots */}
                <div>
                  <Label className="mb-2 block">Available Time Slots</Label>
                  {!selectedDate ? (
                    <div className="text-center py-12 text-muted-foreground border rounded-lg">
                      <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>Select a date to see available slots</p>
                    </div>
                  ) : availableSlots.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground border rounded-lg">
                      <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>No slots available on this date</p>
                      <p className="text-sm">Try selecting a different date</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2 max-h-[300px] overflow-y-auto">
                      {availableSlots.map((slot) => (
                        <button
                          key={slot.slot_id}
                          onClick={() => setSelectedSlot(slot)}
                          className={`p-3 rounded-lg border text-sm transition-colors ${
                            selectedSlot?.slot_id === slot.slot_id
                              ? "bg-primary text-primary-foreground border-primary"
                              : "hover:border-primary/50 hover:bg-primary/5"
                          }`}
                        >
                          <div className="font-medium">
                            {slot.start_time} - {slot.end_time}
                          </div>
                          <div className={`text-xs ${selectedSlot?.slot_id === slot.slot_id ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                            {slot.available_capacity} spot{slot.available_capacity > 1 ? "s" : ""} left
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-between mt-6">
                <Button variant="outline" onClick={() => setStep(2)}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button
                  onClick={() => setStep(4)}
                  disabled={!selectedDate || !selectedSlot}
                  data-testid="next-step-btn"
                >
                  Continue
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Contact Details */}
        {step === 4 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                Your Contact Details
              </CardTitle>
              <CardDescription>How can we reach you?</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Your Name *</Label>
                  <Input
                    value={formData.customer_name}
                    onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                    placeholder="John Doe"
                    data-testid="customer-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone Number *</Label>
                  <Input
                    value={formData.customer_mobile}
                    onChange={(e) => setFormData({ ...formData, customer_mobile: e.target.value })}
                    placeholder="9876543210"
                    data-testid="customer-phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email (Optional)</Label>
                  <Input
                    type="email"
                    value={formData.customer_email}
                    onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                    placeholder="john@example.com"
                  />
                </div>
              </div>

              {/* Device details */}
              <div className="border-t pt-4">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Smartphone className="w-4 h-4" />
                  Device Details (Optional)
                </h4>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Brand</Label>
                    <Input
                      value={formData.brand}
                      onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                      placeholder="e.g., Samsung, Apple, Dell"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Model</Label>
                    <Input
                      value={formData.model}
                      onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                      placeholder="e.g., Galaxy S21, iPhone 13"
                    />
                  </div>
                </div>
              </div>

              {shopInfo?.booking_instructions && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> {shopInfo.booking_instructions}
                  </p>
                </div>
              )}

              <div className="flex justify-between mt-6">
                <Button variant="outline" onClick={() => setStep(3)}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button
                  onClick={() => setStep(5)}
                  disabled={!formData.customer_name.trim() || !formData.customer_mobile.trim()}
                  data-testid="next-step-btn"
                >
                  Review Booking
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 5: Review & Confirm */}
        {step === 5 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-primary" />
                Review Your Booking
              </CardTitle>
              <CardDescription>Please verify all details before confirming</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Device & Services */}
              <div className="bg-muted rounded-lg p-4">
                <h4 className="font-medium mb-2">Device & Issue</h4>
                <p className="text-sm">
                  <strong>Device:</strong> {selectedDeviceType === "Other" ? otherDeviceType : selectedDeviceType}
                  {formData.brand && ` - ${formData.brand}`}
                  {formData.model && ` ${formData.model}`}
                </p>
                {selectedServices.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {selectedServices.map((serviceId) => {
                      const service = services.find((s) => s.id === serviceId);
                      return service ? (
                        <Badge key={serviceId} variant="secondary" className="text-xs">
                          {service.name}
                        </Badge>
                      ) : null;
                    })}
                  </div>
                )}
                {hasOtherIssue && otherIssueDescription && (
                  <p className="text-sm mt-2 text-muted-foreground">
                    <strong>Issue:</strong> {otherIssueDescription}
                  </p>
                )}
              </div>

              {/* Appointment */}
              <div className="bg-primary/5 rounded-lg p-4">
                <h4 className="font-medium mb-2">Appointment</h4>
                <div className="grid sm:grid-cols-2 gap-2 text-sm">
                  <div className="flex items-center gap-2">
                    <CalendarDays className="w-4 h-4 text-muted-foreground" />
                    <span>
                      {selectedDate?.toLocaleDateString("en-US", {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-muted-foreground" />
                    <span>{selectedSlot?.start_time} - {selectedSlot?.end_time}</span>
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div className="space-y-1 text-sm">
                <h4 className="font-medium">Contact Information</h4>
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-muted-foreground" />
                  {formData.customer_name}
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-muted-foreground" />
                  {formData.customer_mobile}
                </div>
                {formData.customer_email && (
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    {formData.customer_email}
                  </div>
                )}
              </div>

              <div className="flex justify-between mt-6">
                <Button variant="outline" onClick={() => setStep(4)}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button 
                  onClick={handleSubmit} 
                  disabled={submitting}
                  data-testid="confirm-booking-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Booking...
                    </>
                  ) : (
                    "Confirm Booking"
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Booking Confirmation Modal */}
      <Dialog open={showConfirmation} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-md" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle2 className="w-6 h-6" />
              Booking Confirmed!
            </DialogTitle>
            <DialogDescription>
              Your appointment has been booked successfully
            </DialogDescription>
          </DialogHeader>
          {bookingResult && (
            <div className="space-y-4 py-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <p className="text-sm text-green-800 mb-1">Booking Number</p>
                <p className="text-2xl font-bold text-green-900">{bookingResult.booking_number}</p>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Shop</span>
                  <span className="font-medium">{bookingResult.shop_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Date</span>
                  <span className="font-medium">
                    {new Date(bookingResult.appointment_date).toLocaleDateString("en-US", {
                      weekday: "short",
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Time</span>
                  <span className="font-medium">{bookingResult.appointment_slot}</span>
                </div>
                {bookingResult.shop_phone && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Contact</span>
                    <a href={`tel:${bookingResult.shop_phone}`} className="font-medium text-primary">
                      {bookingResult.shop_phone}
                    </a>
                  </div>
                )}
              </div>

              {bookingResult.confirmation_message && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">{bookingResult.confirmation_message}</p>
                </div>
              )}

              <Button
                className="w-full"
                onClick={() => goBack()}
                data-testid="done-btn"
              >
                Done
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
